import { stubTrue } from 'lodash';
import { sortBy, uniqBy } from 'lodash/fp';
import pipe from 'lodash/fp/pipe';

import type { AnyObj } from '../../../../typescript';
import type { DataWithMeta } from '../../research/v1';
import { PaginatedResponseV2 } from '../../research/v2';

const selectSingle = <T>(res: DataWithMeta<T>) => {
    if (!res?.data || res._meta.total === 0 || !res._meta.success) {
        return undefined;
    }

    return res.data;
};

const selectAsArrayWithTotal = <T>(res: DataWithMeta<T[]>) => {
    if (!res?.data || res._meta.total === 0 || !res._meta.success) {
        return { items: [], totalCount: 0 };
    }

    return { items: res.data, totalCount: res._meta.total };
};

type OptionType<T, Label extends keyof T, Value extends keyof T> = {
    label: T[Label];
    value: T[Value];
};

const createSelectAsOptions = <T extends AnyObj, Label extends keyof T = keyof T, Value extends keyof T = keyof T>({
    label,
    value,
}: {
    label: Label;
    value: Value;
}) => {
    return pipe<[res: DataWithMeta<T[]>], ReturnType<typeof selectAsArrayWithTotal<T>>, OptionType<T, Label, Value>[]>(
        selectAsArrayWithTotal,
        ({ items }) => items.map(item => ({ label: item[label], value: item[value] })),
    );
};

const createSelectSortedUniq = <T extends AnyObj, Label extends keyof T, Value extends keyof T>({
    label,
    value,
    excludeValues = [],
    uniqueKey,
    sortKey,
}: {
    uniqueKey: keyof T;
    sortKey: keyof T;
    label: Label;
    value: Value;
    excludeValues?: T[Value][];
}) => {
    return pipe<[res: DataWithMeta<T[]>], T[], T[], T[], OptionType<T, Label, Value>[], OptionType<T, Label, Value>[]>(
        ({ data }) => data,
        sortBy(item => item[sortKey]),
        uniqBy(item => item[uniqueKey]),
        data => data.map(item => ({ label: item[label], value: item[value] })),
        data => data.filter(item => !excludeValues?.includes(item.value)),
    );
};

type AbstractSelectorOptions<T> = {
    filter?: (item: T) => boolean;
    limit?: number;
};

const createSelectItems = <T>(props?: AbstractSelectorOptions<T>) => {
    const { filter = stubTrue, limit } = props ?? {};
    return (res: PaginatedResponseV2<T>) => ({
        page: res.page,
        total: res.total,
        items: res.data.filter(filter ?? stubTrue).slice(0, limit ?? res.data.length),
    });
};

const querySelectors = {
    asSingle: selectSingle,
    asArrayWithTotal: selectAsArrayWithTotal,
    asOptions: <T extends AnyObj>(res: DataWithMeta<T[]>) =>
        createSelectAsOptions<T, 'title', 'id'>({ label: 'title', value: 'id' })(res),
    asSortedUniqUsers: <T extends AnyObj>(excludedUserIds?: T['userId'][]) =>
        createSelectSortedUniq<T, 'userName', 'userId'>({
            label: 'userName',
            value: 'userId',
            uniqueKey: 'userId',
            sortKey: 'userName',
            excludeValues: excludedUserIds,
        }),
    _custom: {
        asOptions: createSelectAsOptions,
        asSortedUnique: createSelectSortedUniq,
        /**
         * Custom selectors strictly typed to support research v2 API.
         **/
        researchV2: {
            /**
             * Create a custom selector that returns an array of items.
             */
            asItems: createSelectItems,
        },
    },
};

export { querySelectors };
